import React, { RefObject, useImperativeHandle, useRef } from 'react';
import { View, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { MB_PortalHost } from '@mightybyte/rnw.components.portal';

const DEFAULT_TOOLTIP_PORTAL_NAME = 'MB_TextInputToolTipPortalHost';

interface MB_TextInputToolTipPortalRef {
    /**
     * Returns the location of the component in the window.
     */
    getPortalWindowMeasure: () => Promise<{ x: number, y: number }>

    /**
     * Returns the portal's name.
     */
    getPortalName: () => string
}

interface MB_TextInputToolTipPortal {
    /**
     * Style of the wrapper view.
     */
    wrapperStyle?: StyleProp<ViewStyle>

    /**
     * Pointer events of the wrapper view.
     */
    wrapperPointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only' | undefined

    /**
     * Reference of this component.
     */
    MB_Ref: RefObject<MB_TextInputToolTipPortalRef>

    /**
     * Name to use as the portal's identifier.
     */
    customPortalName?: string
}

/**
 * This is a portal component intended to be used with MB_TextInput on focus tool-tips.
 * Place this component at the very bottom of one of the parent view of the MB_TextInput componnet
 * and just pass in the ref of this component to toolTipData of MB_TextInput.
 * Please be aware that you don`t need to use a separate MB_TextInputToolTipPortal for each MB_TextInput.
 * As long as there is one present in the parent of MB_TextInput, that should be more than enough.
 * NOTE: This component must correctly fills the entire parent of MB_TextInput in order to function properly.
 */
const MB_TextInputToolTipPortal = ({ MB_Ref, wrapperStyle, wrapperPointerEvents, customPortalName }: MB_TextInputToolTipPortal) => {
    const wrapperRef = useRef<View>(null);

    useImperativeHandle(MB_Ref, () => ({
        getPortalWindowMeasure: async () => {
            return new Promise<{ x: number, y: number }>((promise) => {
                wrapperRef?.current?.measureInWindow((x, y) => {
                    promise({ x, y });
                });
            });
        },
        getPortalName: () => customPortalName ?? DEFAULT_TOOLTIP_PORTAL_NAME,
    }));


    return (
        <View
            ref={wrapperRef}
            style={[styles.wrapper, wrapperStyle]} pointerEvents={wrapperPointerEvents ?? 'none'}
        >
            <MB_PortalHost name={customPortalName ?? DEFAULT_TOOLTIP_PORTAL_NAME} />
        </View>
    );
};

export { MB_TextInputToolTipPortal, DEFAULT_TOOLTIP_PORTAL_NAME };
export type { MB_TextInputToolTipPortalRef };

const styles = StyleSheet.create({
    wrapper: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 1,
    },
});
